import { Button, Input, Label, Text, toast } from "@medusajs/ui"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useRef, useState, type ReactNode } from "react"
import { AuthShell, CodeInput } from "@/components/auth-shell"
import { Section } from "@/components/ui"
import { logout } from "@/lib/api"
import { useCurrentMe } from "@/lib/permissions"
import { errorMessage, sdk } from "@/lib/sdk"

/**
 * Two-step sign-in (backend lib/two-factor.ts): an authenticator app, or text-message codes when
 * the store has Twilio Verify set up. The sign-in step after the password, the setup screen when
 * the store requires it, and the "Your account" card.
 */
const post = <T,>(body: Record<string, unknown>) => sdk.client.fetch<T>("/admin/terify/me/2fa", { method: "POST", body })

type Method = "app" | "sms"
type TwoFactorInfo = { enabled: boolean; method: Method | null; phone_hint: string | null; recovery_left: number; sms_available: boolean }

/** A countdown for "Resend code", so nobody burns through the texting limit by double-clicking. */
function useCooldown() {
  const [left, setLeft] = useState(0)
  useEffect(() => {
    if (left <= 0) return
    const t = setTimeout(() => setLeft(left - 1), 1000)
    return () => clearTimeout(t)
  }, [left])
  return [left, () => setLeft(30)] as const
}

function LinkButton({ onClick, children, disabled, muted }: { onClick: () => void; children: ReactNode; disabled?: boolean; muted?: boolean }) {
  return (
    <button type="button" disabled={disabled} onClick={onClick} className={`txt-compact-small-plus hover:underline disabled:no-underline disabled:opacity-50 ${muted ? "text-ui-fg-subtle" : "text-ui-fg-interactive"}`}>
      {children}
    </button>
  )
}

// ── Sign-in step ─────────────────────────────────────────────────────────────

export function TwoFactorPrompt({ email, method, phoneHint }: { email: string; method: Method | null; phoneHint: string | null }) {
  const qc = useQueryClient()
  const sms = method === "sms"
  const [code, setCode] = useState("")
  const [recovery, setRecovery] = useState(false)
  const [cooldown, startCooldown] = useCooldown()
  const send = useMutation({
    mutationFn: () => post<{ phone_hint: string }>({ action: "sms_send" }),
    onSuccess: () => startCooldown(),
    onError: (e) => toast.error(errorMessage(e)),
  })
  // Text the code as soon as the screen opens (once), like Shopify.
  const sent = useRef(false)
  useEffect(() => {
    if (sms && !sent.current) {
      sent.current = true
      send.mutate()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sms])
  const verify = useMutation({
    mutationFn: (c: string) => post<{ verified: boolean; used_recovery: boolean }>({ action: "verify", code: c.trim() }),
    onSuccess: (r) => {
      if (r.used_recovery) toast.warning("Recovery code used. Each one works once. If you lost your phone, set up two-step verification again in Your account.")
      qc.invalidateQueries()
    },
    onError: (e) => (toast.error(errorMessage(e)), setCode("")),
  })
  const submit = (c = code) => c.trim() && !verify.isPending && verify.mutate(c)

  return (
    <AuthShell
      title="Two-step verification"
      subtitle={
        recovery ? (
          "Enter one of the recovery codes you saved when you set up two-step verification."
        ) : sms ? (
          <>We texted a 6-digit code to {phoneHint ?? "your phone"}. It expires in 10 minutes.</>
        ) : (
          <>Enter the 6-digit code from your authenticator app for <span className="text-ui-fg-base">{email}</span>.</>
        )
      }
      footer={
        <LinkButton muted onClick={() => logout()}>
          Sign out
        </LinkButton>
      }
    >
      <form className="flex flex-col gap-y-5" onSubmit={(e) => (e.preventDefault(), submit())}>
        {recovery ? (
          <div className="flex flex-col gap-y-1.5">
            <Label htmlFor="recovery-code">Recovery code</Label>
            <Input id="recovery-code" autoFocus autoComplete="off" spellCheck={false} placeholder="xxxxx-xxxxx" maxLength={20} value={code} onChange={(e) => setCode(e.target.value)} />
          </div>
        ) : (
          <CodeInput value={code} onChange={setCode} onComplete={(v) => submit(v)} disabled={verify.isPending} label={sms ? "Code from the text message" : "Code from your authenticator app"} />
        )}
        <Button type="submit" className="w-full" isLoading={verify.isPending} disabled={recovery ? !code.trim() : code.length !== 6}>
          Verify
        </Button>
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
          {sms && !recovery ? (
            <LinkButton disabled={send.isPending || cooldown > 0} onClick={() => send.mutate()}>
              {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
            </LinkButton>
          ) : null}
          <LinkButton onClick={() => (setRecovery(!recovery), setCode(""))}>{recovery ? (sms ? "Use a texted code" : "Use authenticator app") : "Use a recovery code"}</LinkButton>
        </div>
      </form>
    </AuthShell>
  )
}

// ── Setup (shared by the required-setup screen and Your account) ───────────────

function MethodChoice({ value, onChange, smsAvailable }: { value: Method; onChange: (m: Method) => void; smsAvailable: boolean }) {
  const options: { id: Method; title: string; body: string; badge?: string }[] = [
    { id: "app", title: "Authenticator app", body: "Google Authenticator, 1Password, Authy… Works without signal.", badge: "Recommended" },
    ...(smsAvailable ? [{ id: "sms" as const, title: "Text message", body: "We text a code to your mobile number each time you sign in." }] : []),
  ]
  if (options.length < 2) return null
  return (
    <div role="radiogroup" aria-label="How do you want to get your codes?" className="grid gap-2">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          role="radio"
          aria-checked={value === o.id}
          onClick={() => onChange(o.id)}
          className={`flex items-start gap-3 rounded-lg border p-3 text-left transition ${value === o.id ? "border-ui-border-interactive bg-ui-bg-highlight" : "border-ui-border-base hover:bg-ui-bg-base-hover"}`}
        >
          <span className={`mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border ${value === o.id ? "border-ui-border-interactive" : "border-ui-border-strong"}`}>
            {value === o.id ? <span className="size-2 rounded-full bg-ui-fg-interactive" /> : null}
          </span>
          <span className="min-w-0">
            <span className="flex items-center gap-2">
              <Text size="small" weight="plus">{o.title}</Text>
              {o.badge ? <span className="tf-pill" style={{ background: "#eaf4ff", color: "#00527c" }}>{o.badge}</span> : null}
            </span>
            <Text size="xsmall" className="text-ui-fg-subtle">{o.body}</Text>
          </span>
        </button>
      ))}
    </div>
  )
}

function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const [saved, setSaved] = useState(false)
  const download = () => {
    const blob = new Blob([`Keel Admin recovery codes\nEach code works once.\n\n${codes.join("\n")}\n`], { type: "text/plain" })
    const a = document.createElement("a")
    a.href = URL.createObjectURL(blob)
    a.download = "keel-recovery-codes.txt"
    a.click()
    URL.revokeObjectURL(a.href)
  }
  return (
    <div className="flex flex-col gap-3">
      <div>
        <Text size="small" weight="plus">Save your recovery codes</Text>
        <Text size="xsmall" className="text-ui-fg-subtle">If you lose your phone, each code lets you sign in once. They won't be shown again.</Text>
      </div>
      <pre className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-lg bg-ui-bg-subtle p-3 text-center font-mono text-[13px]">{codes.map((c) => <span key={c}>{c}</span>)}</pre>
      <div className="flex gap-2">
        <Button size="small" variant="secondary" type="button" onClick={download}>Download</Button>
        <Button size="small" variant="secondary" type="button" onClick={() => navigator.clipboard.writeText(codes.join("\n")).then(() => toast.success("Copied"))}>Copy</Button>
      </div>
      <label className="flex items-center gap-2 text-[13px]">
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} /> I saved these codes somewhere safe
      </label>
      <Button type="button" className="w-full" disabled={!saved} onClick={onDone}>Continue</Button>
    </div>
  )
}

function AppSetup({ onEnabled, onCancel }: { onEnabled: (codes: string[]) => void; onCancel?: () => void }) {
  const [setup, setSetup] = useState<{ secret: string; otpauth_url: string } | null>(null)
  const [qr, setQr] = useState<string | null>(null)
  const [code, setCode] = useState("")
  const start = useMutation({ mutationFn: () => post<{ secret: string; otpauth_url: string }>({ action: "setup" }), onSuccess: setSetup, onError: (e) => toast.error(errorMessage(e)) })
  const enable = useMutation({
    mutationFn: (c: string) => post<{ recovery_codes: string[] }>({ action: "enable", code: c }),
    onSuccess: (r) => onEnabled(r.recovery_codes),
    onError: (e) => (toast.error(errorMessage(e)), setCode("")),
  })
  // One secret per mount (a ref, so React's dev double-run doesn't create two).
  const started = useRef(false)
  useEffect(() => {
    if (started.current) return
    started.current = true
    start.mutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => {
    if (!setup) return setQr(null)
    import("qrcode").then((m) => m.toDataURL(setup.otpauth_url, { margin: 1, width: 176 }).then(setQr)).catch(() => setQr(null))
  }, [setup])

  return (
    <form className="flex flex-col gap-4" onSubmit={(e) => (e.preventDefault(), code.length === 6 && enable.mutate(code))}>
      <div className="flex flex-col items-center gap-2">
        <Text size="small" className="self-start">1. Scan this QR code with your authenticator app.</Text>
        {qr ? <img src={qr} alt="QR code for your authenticator app" width={176} height={176} className="rounded-lg border border-ui-border-base" /> : <div className="size-[176px] animate-pulse rounded-lg bg-ui-bg-subtle" />}
        {setup ? (
          <Text size="xsmall" className="text-center text-ui-fg-subtle">
            Can't scan? Enter this key instead:
            <br />
            <button type="button" title="Copy" className="mt-1 select-all break-all font-mono text-ui-fg-base hover:underline" onClick={() => navigator.clipboard.writeText(setup.secret).then(() => toast.success("Key copied"))}>
              {setup.secret.replace(/(.{4})/g, "$1 ").trim()}
            </button>
          </Text>
        ) : null}
      </div>
      <Text size="small">2. Enter the 6-digit code it shows.</Text>
      <CodeInput value={code} onChange={setCode} onComplete={(v) => !enable.isPending && enable.mutate(v)} disabled={!setup || enable.isPending} label="Code from your authenticator app" />
      <div className="flex gap-2">
        <Button type="submit" className="flex-1" isLoading={enable.isPending} disabled={code.length !== 6}>Turn on</Button>
        {onCancel ? <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button> : null}
      </div>
    </form>
  )
}

function SmsSetup({ onEnabled, onCancel }: { onEnabled: (codes: string[]) => void; onCancel?: () => void }) {
  const [phone, setPhone] = useState("")
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [code, setCode] = useState("")
  const [cooldown, startCooldown] = useCooldown()
  const send = useMutation({
    mutationFn: () => post<{ phone_hint: string }>({ action: "sms_setup", phone }),
    onSuccess: (r) => (setSentTo(r.phone_hint), setCode(""), startCooldown()),
    onError: (e) => toast.error(errorMessage(e)),
  })
  const enable = useMutation({
    mutationFn: (c: string) => post<{ recovery_codes: string[] }>({ action: "sms_enable", code: c }),
    onSuccess: (r) => onEnabled(r.recovery_codes),
    onError: (e) => (toast.error(errorMessage(e)), setCode("")),
  })
  if (!sentTo) {
    return (
      <form className="flex flex-col gap-4" onSubmit={(e) => (e.preventDefault(), phone.trim() && send.mutate())}>
        <div className="flex flex-col gap-y-1.5">
          <Label htmlFor="tf-phone">Mobile number</Label>
          <Input id="tf-phone" type="tel" autoComplete="tel" autoFocus placeholder="(555) 123-4567" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <Text size="xsmall" className="text-ui-fg-subtle">US numbers as usual; other countries start with + and the country code. Message and data rates may apply.</Text>
        </div>
        <div className="flex gap-2">
          <Button type="submit" className="flex-1" isLoading={send.isPending} disabled={!phone.trim()}>Text me a code</Button>
          {onCancel ? <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button> : null}
        </div>
      </form>
    )
  }
  return (
    <form className="flex flex-col gap-4" onSubmit={(e) => (e.preventDefault(), code.length === 6 && enable.mutate(code))}>
      <Text size="small" className="text-center">Enter the 6-digit code we texted to {sentTo}.</Text>
      <CodeInput value={code} onChange={setCode} onComplete={(v) => !enable.isPending && enable.mutate(v)} disabled={enable.isPending} label="Code from the text message" />
      <Button type="submit" isLoading={enable.isPending} disabled={code.length !== 6}>Turn on</Button>
      <div className="flex justify-center gap-4">
        <LinkButton disabled={send.isPending || cooldown > 0} onClick={() => send.mutate()}>{cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}</LinkButton>
        <LinkButton onClick={() => (setSentTo(null), setCode(""))}>Change number</LinkButton>
      </div>
    </form>
  )
}

/** Choose a method, set it up, save the recovery codes. */
function SetupFlow({ smsAvailable, onDone, onCancel }: { smsAvailable: boolean; onDone: () => void; onCancel?: () => void }) {
  const qc = useQueryClient()
  const [method, setMethod] = useState<Method>("app")
  const [chosen, setChosen] = useState(!smsAvailable)
  const [codes, setCodes] = useState<string[] | null>(null)
  const enabled = (c: string[]) => (setCodes(c), qc.invalidateQueries({ queryKey: ["2fa"] }), toast.success("Two-step verification is on"))
  if (codes) return <RecoveryCodes codes={codes} onDone={onDone} />
  if (!chosen) {
    return (
      <div className="flex flex-col gap-4">
        <MethodChoice value={method} onChange={setMethod} smsAvailable={smsAvailable} />
        <div className="flex gap-2">
          <Button type="button" className="flex-1" onClick={() => setChosen(true)}>Continue</Button>
          {onCancel ? <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button> : null}
        </div>
      </div>
    )
  }
  const back = smsAvailable ? () => setChosen(false) : onCancel
  return method === "sms" ? <SmsSetup onEnabled={enabled} onCancel={back} /> : <AppSetup onEnabled={enabled} onCancel={back} />
}

/** Full-page step when the store requires two-step verification and this account hasn't set it up. */
export function TwoFactorSetupGate({ email, smsAvailable }: { email: string; smsAvailable: boolean }) {
  const qc = useQueryClient()
  return (
    <AuthShell
      wide
      title="Set up two-step verification"
      subtitle={<>This store requires it for every account. After your password, you'll enter a code from your phone each time you sign in as <span className="text-ui-fg-base">{email}</span>.</>}
      footer={<LinkButton muted onClick={() => logout()}>Sign out</LinkButton>}
    >
      <SetupFlow smsAvailable={smsAvailable} onDone={() => qc.invalidateQueries()} />
    </AuthShell>
  )
}

// ── Your account card ─────────────────────────────────────────────────────────

export function TwoFactorCard() {
  const qc = useQueryClient()
  const me = useCurrentMe()
  const required = !!me.two_factor?.required
  const state = useQuery({ queryKey: ["2fa"], queryFn: () => sdk.client.fetch<TwoFactorInfo>("/admin/terify/me/2fa") })
  const [settingUp, setSettingUp] = useState(false)
  const [code, setCode] = useState("")
  const [disabling, setDisabling] = useState(false)
  const [cooldown, startCooldown] = useCooldown()
  const done = () => (qc.invalidateQueries({ queryKey: ["2fa"] }), qc.invalidateQueries({ queryKey: ["me"] }))
  const d = state.data
  const sms = d?.method === "sms"

  const send = useMutation({ mutationFn: () => post({ action: "sms_send" }), onSuccess: () => (startCooldown(), toast.success(`Code texted to ${d?.phone_hint}`)), onError: (e) => toast.error(errorMessage(e)) })
  const disable = useMutation({
    mutationFn: () => post({ action: "disable", code: code.trim() }),
    onSuccess: () => (setDisabling(false), setCode(""), done(), toast.success(required ? "Removed. Set up the new one now." : "Two-step verification is off")),
    onError: (e) => toast.error(errorMessage(e)),
  })

  return (
    <Section title="Two-step verification">
      <div className="flex flex-col gap-3 px-4 py-4 text-[13px]">
        <Text size="small" className="text-ui-fg-subtle">
          Protect your account with a code from your phone (an authenticator app{d?.sms_available ? " or a text message" : ""}) every time you sign in on a new session.
          {required ? " Required by this store for every account." : ""}
        </Text>
        {settingUp && d ? (
          <div className="max-w-sm">
            <SetupFlow smsAvailable={d.sms_available} onCancel={() => setSettingUp(false)} onDone={() => (setSettingUp(false), done())} />
          </div>
        ) : d?.enabled ? (
          <div className="flex flex-col gap-2">
            <span className="flex flex-wrap items-center gap-2">
              <span className="tf-pill" style={{ background: "#affebf", color: "#014b40" }}>On</span>
              {sms ? `Text messages to ${d.phone_hint}` : "Authenticator app"} · {d.recovery_left} recovery codes left
            </span>
            {disabling ? (
              <form className="flex flex-wrap items-center gap-2" onSubmit={(e) => (e.preventDefault(), disable.mutate())}>
                <Input aria-label={sms ? "Texted code or recovery code" : "Current code or recovery code"} placeholder={sms ? "Texted code" : "Current code"} autoComplete="one-time-code" className="w-40" value={code} onChange={(e) => setCode(e.target.value)} />
                <Button size="small" variant="danger" type="submit" isLoading={disable.isPending} disabled={!code.trim()}>{required ? "Remove" : "Turn off"}</Button>
                <Button size="small" variant="transparent" type="button" onClick={() => setDisabling(false)}>Cancel</Button>
                {sms ? <LinkButton disabled={send.isPending || cooldown > 0} onClick={() => send.mutate()}>{cooldown > 0 ? `Text again in ${cooldown}s` : "Text me a code"}</LinkButton> : null}
                {required ? <Text size="xsmall" className="w-full text-ui-fg-subtle">You'll set up your new phone or number right after.</Text> : null}
              </form>
            ) : (
              <div><Button size="small" variant="secondary" onClick={() => setDisabling(true)}>{required ? "Change phone or method" : "Turn off"}</Button></div>
            )}
          </div>
        ) : d ? (
          <div><Button size="small" variant="secondary" onClick={() => setSettingUp(true)}>Set up two-step verification</Button></div>
        ) : null}
      </div>
    </Section>
  )
}
